import { Box, Button, CloseButton, Flex, Stack, Text } from '@mantine/core';
import { FieldValues, useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TravelExpenseSchema, defaultTravelExpenseValues } from '@/module/Travel/Request/model/TravelRequestSchema';
import { TravelActivityType, travelExpenseKey } from '@/module/Travel/Request/model/TravelRequestTypes';

import { FormControlInput, NumberInputControl, TextInputControl } from '@/core/components';

import { CurrencySelectControl } from '@feature/utils';

import { TravelExpenseTypeSelectControl } from './TravelExpenseTypeSelectControl';
import { TravelFundingSourceSelectControl } from './TravelFundingSourceSelectControl';

// The backend refuses any other currency on submission, and a RON row carries its EUR equivalent alongside.
const travelExpenseCurrencies = ['RON', 'EUR'] as const;

export interface TravelExpenseListControlProps<T extends FieldValues> extends FormControlInput<T> {
  label: string;

  description?: string;
  isDisabled?: boolean;

  activityType: TravelActivityType;
}

interface TravelExpenseRowProps<T extends FieldValues> extends FormControlInput<T> {
  index: number;
  position: number;
  isDisabled?: boolean;

  activityType: TravelActivityType;
  sourceRepeated: boolean;

  onRemove: () => void;
}

function TravelExpenseRow<T extends FieldValues>(props: TravelExpenseRowProps<T>) {
  const { name, index, position, control, isSubmitting, isDisabled, activityType, sourceRepeated, onRemove } = props;
  const { t } = useTranslation();
  const fundingSource: unknown = useWatch({
    control,
    // @ts-expect-error fix later
    name: `${name}.${index}.fundingSource`,
  });
  const currency: unknown = useWatch({
    control,
    // @ts-expect-error fix later
    name: `${name}.${index}.currency`,
  });
  const isRon = currency === 'RON';

  return (
    <Stack
      gap="xs"
      role="group"
      aria-label={t('travel.requests.form.expenseLine', { index: position })}
    >
      <Flex
        align="center"
        gap="4px"
      >
        <Text
          mr="8px"
          mt="25px"
          size="sm"
        >
          #{position}
        </Text>
        {/* @ts-expect-error fix later */}
        <TravelExpenseTypeSelectControl
          label={t('travel.requests.form.fields.expenseType')}
          control={control}
          name={`${name}.${index}.expenseType`}
          isSubmitting={isSubmitting}
          isDisabled={isDisabled}
          withAsterisk
        />
        {/* @ts-expect-error fix later */}
        <TravelFundingSourceSelectControl
          label={t('travel.requests.form.fields.fundingSource')}
          control={control}
          name={`${name}.${index}.fundingSource`}
          activityType={activityType}
          isSubmitting={isSubmitting}
          isDisabled={isDisabled}
          repeated={sourceRepeated}
        />
        {/* @ts-expect-error fix later */}
        <NumberInputControl
          label={t('travel.requests.form.fields.requestedAmount')}
          control={control}
          name={`${name}.${index}.requestedAmount`}
          isSubmitting={isSubmitting}
          isDisabled={isDisabled}
          allowDecimal
          withAsterisk
        />
        {/* @ts-expect-error fix later */}
        <CurrencySelectControl
          label={t('travel.requests.form.fields.currency')}
          control={control}
          name={`${name}.${index}.currency`}
          currencies={travelExpenseCurrencies}
          isSubmitting={isSubmitting}
          isDisabled={isDisabled}
          withAsterisk
        />
        {isRon && (
          // @ts-expect-error fix later
          <NumberInputControl
            label={t('travel.requests.form.fields.eurAmount')}
            control={control}
            name={`${name}.${index}.eurAmount`}
            isSubmitting={isSubmitting}
            isDisabled={isDisabled}
            allowDecimal
            withAsterisk
          />
        )}
        <CloseButton
          mt="25px"
          aria-label={t('button.delete')}
          onClick={onRemove}
          c="red"
        />
      </Flex>
      {fundingSource === 'PROJECT_FUNDS' && (
        <Flex
          align="center"
          gap="4px"
        >
          {/* @ts-expect-error fix later */}
          <TextInputControl
            label={t('travel.requests.form.fields.projectNumber')}
            control={control}
            name={`${name}.${index}.projectNumber`}
            isSubmitting={isSubmitting}
            isDisabled={isDisabled}
          />
          {/* @ts-expect-error fix later */}
          <TextInputControl
            label={t('travel.requests.form.fields.projectTitle')}
            control={control}
            name={`${name}.${index}.projectTitle`}
            isSubmitting={isSubmitting}
            isDisabled={isDisabled}
          />
          {/* @ts-expect-error fix later */}
          <NumberInputControl
            label={t('travel.requests.form.fields.projectAmount')}
            control={control}
            name={`${name}.${index}.projectAmount`}
            isSubmitting={isSubmitting}
            isDisabled={isDisabled}
            allowDecimal
          />
          {isRon && (
            // @ts-expect-error fix later
            <NumberInputControl
              label={t('travel.requests.form.fields.projectEurAmount')}
              control={control}
              name={`${name}.${index}.projectEurAmount`}
              isSubmitting={isSubmitting}
              isDisabled={isDisabled}
              allowDecimal
              withAsterisk
            />
          )}
        </Flex>
      )}
    </Stack>
  );
}

export function TravelExpenseListControl<T extends FieldValues>(props: TravelExpenseListControlProps<T>) {
  const { label, description, name, isSubmitting, isDisabled, control, activityType } = props;
  /* @ts-expect-error fix later */
  const { fields, append, remove } = useFieldArray({ name, keyName: 'uuid', control });
  const { t } = useTranslation();
  const expenses: unknown = useWatch({ control, name });
  const rows: (TravelExpenseSchema | undefined)[] = Array.isArray(expenses) ? expenses : [];
  const expenseKeys = rows.map(expense => (expense == null ? null : travelExpenseKey(expense)));
  // Derived from the rows, because the schema reaches its repeat rule only once the whole request parses.
  const sourceRepeated = (index: number) => {
    const key = expenseKeys[index];
    return key != null && expenseKeys.indexOf(key) !== expenseKeys.lastIndexOf(key);
  };
  // The mobility support line has its own card, so this list skips it and numbers the rest.
  const listed = fields
    .map((field, index) => ({ field, index }))
    .filter(({ index }) => rows[index]?.expenseType !== 'UMFST_MOBILITY_SUPPORT');

  return (
    <Box>
      <Flex justify="space-between">
        <Box>
          <Text
            fw="500"
            size="xl"
          >
            {label}
          </Text>
          {description && <Text c="dimmed">{description}</Text>}
        </Box>
        <Button
          disabled={isDisabled}
          // @ts-expect-error fix later
          onClick={() => append(defaultTravelExpenseValues)}
        >
          {t('travel.requests.form.addExpense')}
        </Button>
      </Flex>
      <Stack gap="xl">
        {listed.length === 0 && (
          <Text
            size="xs"
            c="dimmed"
            fs="italic"
          >
            {t('travel.requests.form.fields.emptyExpenseList')}
          </Text>
        )}
        {listed.map(({ field, index }, position) => (
          <TravelExpenseRow<T>
            key={field.uuid}
            name={name}
            index={index}
            position={position + 1}
            control={control}
            isSubmitting={isSubmitting}
            isDisabled={isDisabled}
            activityType={activityType}
            sourceRepeated={sourceRepeated(index)}
            onRemove={() => remove(index)}
          />
        ))}
      </Stack>
    </Box>
  );
}
